import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { API_ORIGIN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const wsRef = useRef(null);
  const listeners = useRef(new Set());
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const pollTimer = useRef(null);
  const backoffRef = useRef(2500);
  const stoppedRef = useRef(false);

  const [notifications, setNotifications] = useState([]);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [messageUnread, setMessageUnread] = useState(0);
  const [sectionCounts, setSectionCounts] = useState({});

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const emit = useCallback((evt) => {
    listeners.current.forEach((fn) => {
      try { fn(evt); } catch (e) { /* ignore */ }
    });
  }, []);

  const reloadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data.notifications || []);
      setUnreadNotifs(data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const reloadMessageUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/unread-count");
      setMessageUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const reloadSectionCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/section-counts");
      setSectionCounts(data || {});
    } catch (e) { /* ignore */ }
  }, []);

  const markSectionRead = useCallback(async (section) => {
    setSectionCounts((prev) => (prev[section] ? { ...prev, [section]: 0 } : prev));
    try {
      await api.post(`/notifications/section/${section}/read`);
      reloadNotifications();
    } catch (e) { /* ignore */ }
  }, [reloadNotifications]);

  const markNotifRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadNotifs((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`); } catch (e) { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadNotifs(0);
    try { await api.post("/notifications/read-all"); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) {
      stoppedRef.current = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } wsRef.current = null; }
      setNotifications([]); setUnreadNotifs(0); setMessageUnread(0); setSectionCounts({});
      return;
    }
    stoppedRef.current = false;

    const connect = async () => {
      if (stoppedRef.current) return;
      // SEC-18: mint a short-lived single-use ticket via the CSRF-protected,
      // cookie-auth endpoint, then open the socket with it. A FRESH ticket is
      // fetched on EVERY (re)connect attempt (single-use + 30s TTL). No session
      // token or cookie-only auth is used on the socket itself.
      let ticket;
      try {
        const { data } = await api.post("/ws/ticket");
        ticket = data && data.ticket;
      } catch (err) {
        // 401 -> session gone (logged out / expired): stop reconnecting.
        if (err && err.response && err.response.status === 401) {
          stoppedRef.current = true;
          return;
        }
        // Other failures (network / proxy without WS): retry with backoff.
        if (!stoppedRef.current) {
          reconnectTimer.current = setTimeout(connect, backoffRef.current);
          backoffRef.current = Math.min(backoffRef.current * 2, 30000);
        }
        return;
      }
      if (stoppedRef.current || !ticket) return;
      const url = API_ORIGIN.replace(/^http/, "ws") + "/api/ws?ticket=" + encodeURIComponent(ticket);
      let ws;
      try { ws = new WebSocket(url); } catch (e) { return; }
      wsRef.current = ws;
      ws.onopen = () => { backoffRef.current = 2500; };
      ws.onmessage = (e) => {
        let evt;
        try { evt = JSON.parse(e.data); } catch (_) { return; }
        if (evt.type === "notification") {
          if (evt.data && evt.data.notification) {
            setNotifications((prev) => [evt.data.notification, ...prev].slice(0, 60));
          }
          if (evt.data && typeof evt.data.unread === "number") setUnreadNotifs(evt.data.unread);
          reloadSectionCounts();
          // SEC-12: a new team invite (or its acceptance) changes the user's pending
          // invitations → refresh the shared query so the sidebar badge / banner / page
          // update live. Reuses the existing ticket-authed socket; no WS-auth change.
          const nt = evt.data && evt.data.notification && evt.data.notification.type;
          if (nt === "team_invite" || nt === "team_role") {
            queryClient.invalidateQueries({ queryKey: ["my-team-invitations"] });
          }
        } else if (evt.type === "message") {
          setMessageUnread((n) => n + 1);
        }
        emit(evt);
      };
      ws.onclose = () => {
        // Reconnect with capped exponential backoff so a production proxy that
        // doesn't support WebSocket upgrades doesn't spam the console / network.
        if (!stoppedRef.current) {
          reconnectTimer.current = setTimeout(connect, backoffRef.current);
          backoffRef.current = Math.min(backoffRef.current * 2, 30000);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch (_) { /* ignore */ } };
    };

    connect();
    reloadNotifications();
    reloadMessageUnread();
    reloadSectionCounts();
    pingTimer.current = setInterval(() => {
      try { if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send("ping"); } catch (_) { /* ignore */ }
    }, 25000);
    // HTTP polling fallback: when the realtime socket isn't connected (e.g. a
    // production ingress without WebSocket upgrade), keep notification/message
    // counts fresh. No-op while the socket is healthy (preview).
    pollTimer.current = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== 1) {
        reloadNotifications();
        reloadMessageUnread();
        reloadSectionCounts();
      }
    }, 30000);

    return () => {
      stoppedRef.current = true;
      clearTimeout(reconnectTimer.current);
      clearInterval(pingTimer.current);
      clearInterval(pollTimer.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_) { /* ignore */ } wsRef.current = null; }
    };
  }, [user, emit, reloadNotifications, reloadMessageUnread, reloadSectionCounts, queryClient]);

  const value = {
    notifications, unreadNotifs, messageUnread, sectionCounts,
    subscribe, reloadNotifications, reloadMessageUnread, reloadSectionCounts,
    markNotifRead, markAllRead, markSectionRead, setMessageUnread,
  };
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export const useRealtime = () => useContext(RealtimeContext) || {};

export function useRealtimeEvent(callback) {
  const ctx = useContext(RealtimeContext);
  const ref = useRef(callback);
  useEffect(() => { ref.current = callback; });
  useEffect(() => {
    if (!ctx || !ctx.subscribe) return undefined;
    return ctx.subscribe((evt) => ref.current && ref.current(evt));
  }, [ctx]);
}
